//cspell:ignore mensaje
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Checkbox, Flex, If, Text } from '@/lib/v2/components';

import Skeleton from '@/src/modules/CampaignsModule/components/actionManagement/WhatsAppAction/components/WhatsAppForm/components/Skeleton/Skeleton';
import { IWhatsAppFormInfo } from '@/src/modules/CampaignsModule/components/actionManagement/WhatsAppAction/components/WhatsAppForm/hooks/useWhatsAppForm';
import { TEMPLATE_OPTION } from '@/src/modules/CampaignsModule/components/actionManagement/WhatsAppAction/constants';
import { TRANSLATE_WHATSAPP_ACTION_FORM } from '@/src/modules/CampaignsModule/constants';
import { IFormProps } from '@/src/modules/CampaignsModule/interfaces/CampaignActions';

import { TemplateCard } from './components/TemplateCard';
import { TemplateOptions } from './components/TemplateOptions';
import { WhatsAppTemplateInputs } from './components/WhatsAppTemplateInputs';
import { useTemplateManagement } from './hooks/useTemplateV0Management';

import {
  atomActionInfoWhatsApp,
  atomCurrentWhatsAppTemplate,
  atomMessageWhatsApp,
  atomTemplateOption,
} from '@/modules/CampaignsModule/atoms/actionWhatsAppAtom';
import { IWhatsAppTemplate } from '@/modules/CampaignsModule/interfaces/WhatsApp';
import { atomHandleSelectedTemplate } from '@/modules/ContactsModule/atoms/WhatsApp';

interface IMessageInputProps {
  form: IFormProps<IWhatsAppFormInfo>;
  rules: Record<string, RegisterOptions>;
}

const MessageInputV0 = ({ form, rules }: IMessageInputProps) => {
  const { t } = useTranslation('inbox');
  const [isTemplateCreated, setIsTemplateCreated] = useState(false);

  const templateOption = useAtomValue(atomTemplateOption);

  const [messageWhatsApp, setMessageWhatsApp] = useAtom(atomMessageWhatsApp);

  const actionInfoWhatsApp = useAtomValue(atomActionInfoWhatsApp); // This atom contain the previous data for whatsapp action.

  const setAtomHandleSelectedTemplate = useSetAtom(atomHandleSelectedTemplate);
  const setCurrentWhatsAppTemplate = useSetAtom(atomCurrentWhatsAppTemplate);

  const { control, setValue } = form;

  const {
    getPreviousSelectedTemplate,
    selectedTemplate,
    handleSelectedTemplate,
    handleDeleteTemplate,
    getTemplates,
  } = useTemplateManagement({ setValue });

  const isLoadingPreviousData = useMemo(() => {
    return Object.keys(actionInfoWhatsApp).length === 0;
  }, [actionInfoWhatsApp]);

  useLayoutEffect(() => {
    void getTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAtomHandleSelectedTemplate(handleSelectedTemplate);
  }, [handleSelectedTemplate, setAtomHandleSelectedTemplate]);

  useEffect(() => {
    !isLoadingPreviousData && actionInfoWhatsApp.mensaje && getPreviousSelectedTemplate();
  }, [isLoadingPreviousData, actionInfoWhatsApp.mensaje, getPreviousSelectedTemplate]);

  /**
   * This effect is used to show the success message of the template creation
   */
  useEffect(() => {
    setTimeout(() => {
      if (isTemplateCreated) {
        setIsTemplateCreated(false);
      }
    }, 15000);
  }, [isTemplateCreated, setIsTemplateCreated]);

  useEffect(() => {
    if (messageWhatsApp.original.length === 0) {
      setMessageWhatsApp({
        ...messageWhatsApp,
        original: actionInfoWhatsApp?.mensaje ?? '',
      });
      try {
        if (actionInfoWhatsApp?.mensaje) {
          setCurrentWhatsAppTemplate(JSON.parse(actionInfoWhatsApp.mensaje) as IWhatsAppTemplate);
        }
      } catch (error) {
        console.error('Error parsing WhatsApp template JSON:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionInfoWhatsApp.mensaje]);

  const isTemplateIdleAndMessageIsEmpty =
    templateOption === TEMPLATE_OPTION.IDLE && !messageWhatsApp.original;
  const isTemplateCreateMode = templateOption === TEMPLATE_OPTION.CREATE;
  const isTemplateIdleAndMessageIsNotEmpty =
    !!messageWhatsApp.original && templateOption === TEMPLATE_OPTION.IDLE;

  return (
    <>
      <div>
        <Text
          className="sticky top-0 z-[1] border-b border-solid border-emblue-borders bg-emblue-white pb-2"
          color="primary"
          fontWeight="medium"
          variant="sub-headline"
        >
          {t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.message`)}
        </Text>
        <If condition={isTemplateIdleAndMessageIsEmpty}>
          <Text className="mt-4" color="dark" fontWeight="normal" variant="text">
            {t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.messageDisclaimer`)}
          </Text>
        </If>

        <div className="mb-10">
          <If condition={isLoadingPreviousData}>
            <Skeleton className="mt-9 h-10 w-full" />
          </If>
          <If condition={!isLoadingPreviousData}>
            <Flex
              withGap
              className={isTemplateCreateMode ? 'mt-4' : 'mt-9'}
              gapSize="small"
              itemAlignment="start"
              textAlignment="center"
            >
              <If condition={isTemplateIdleAndMessageIsEmpty}>
                <TemplateOptions />
              </If>
              <If condition={isTemplateIdleAndMessageIsNotEmpty}>
                <TemplateCard
                  templateName={selectedTemplate?.friendly_name ?? ''}
                  onDelete={handleDeleteTemplate}
                />
              </If>
              <If condition={isTemplateCreateMode}>
                <WhatsAppTemplateInputs />
              </If>
            </Flex>
          </If>
        </div>
        <If condition={!messageWhatsApp.original}>
          <div className="-mt-1 flex w-full justify-start">
            <Checkbox
              control={control}
              id="whatsapp_template_checkbox"
              name="template"
              rules={rules.template}
              type="hidden"
            />
          </div>
        </If>
        <If condition={isTemplateCreated}>
          <Text className="mt-4" color="tertiary" fontWeight="normal" variant="text">
            {t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.messageSuccessTemplateCreate`)}
          </Text>
        </If>
        <If condition={isTemplateCreated}>
          <Text className="mt-4" color="tertiary" fontWeight="normal" variant="text">
            {t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.messageSuccessTemplateCreate`)}
          </Text>
        </If>
      </div>
    </>
  );
};

export default MessageInputV0;
